/**
 * ViewUser Page
 *
 * Detailed user view page with enhanced header and tabs.
 * Features: user stats (roles, rights, presence, last login), tabs for different information.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconCopy,
  IconShield,
  IconKey,
  IconAlertCircle,
  IconUser,
  IconDevices,
  IconPhone,
  IconHistory,
  IconUserCircle,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  Container,
  Stack,
  Card,
  Group,
  Button,
  Avatar,
  Badge,
  ActionIcon,
  Tabs,
  Alert,
  Center,
  Text,
  Loader,
} from '../../../components/ui';
import { FadeIn, SlideIn } from '../../../components/animations';
import { useHttpClient, useRoleService } from '@actschurch/ccms-sdk';
import { usePresenceManager } from '../../../hooks/usePresenceManager';
import { getApiUrl } from '../../../config/ccms';
import { AccountDetailsTab } from './components/AccountDetailsTab';
import { MemberInfoTab } from './components/MemberInfoTab';
import { RolesRightsTab } from './components/RolesRightsTab';
import { DevicesTab } from './components/DevicesTab';
import { VoipSetupTab } from './components/VoipSetupTab';
import { LoginActivityTab } from './components/LoginActivityTab';
import { DangerZoneTab } from './components/DangerZoneTab';
import { ViewMemberProfileButton } from './components/ViewMemberProfileButton';
import type {
  UserModelView,
  RoleModelView,
  RightModelView,
  LoginAttemptModelView,
  UserPushDevice,
  ResponseData,
} from './types';

export default function ViewUserPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const httpClient = useHttpClient();
  const roleService = useRoleService();
  const { onlineUsersMap, getUserPresence, isConnected } = usePresenceManager();

  // State
  const [user, setUser] = useState<UserModelView | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [allRoles, setAllRoles] = useState<RoleModelView[]>([]);
  const [allRights, setAllRights] = useState<RightModelView[]>([]);
  const [devices, setDevices] = useState<UserPushDevice[]>([]);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttemptModelView[]>([]);
  const [lastLogin, setLastLogin] = useState<LoginAttemptModelView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('account-details');
  const [presenceStatus, setPresenceStatus] = useState<'online' | 'away' | 'busy' | 'offline'>('offline');

  /**
   * Fetch user data
   */
  const fetchUser = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const url = getApiUrl('users', `/api/admin/users/${id}`);
      const response = await httpClient.get<ResponseData<UserModelView>>(url);
      // Try both patterns for response data access
      const userData = (response as any).data?.data || response.data || null;
      setUser(userData);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch user');
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  }, [id, httpClient]);

  /**
   * Fetch user roles
   */
  const fetchUserRoles = useCallback(async () => {
    if (!id) return;

    try {
      const url = getApiUrl('users', `/api/admin/users/${id}/roles`);
      const response = await httpClient.get<ResponseData<string[]>>(url);
      const rolesData = (response as any).data?.data || response.data || [];
      setUserRoles(rolesData);
    } catch (err: any) {
      console.error('Error fetching user roles:', err);
    }
  }, [id, httpClient]);

  /**
   * Fetch all available roles
   */
  const fetchAllRoles = useCallback(async () => {
    try {
      const rolesData = await roleService.getAll();
      setAllRoles(rolesData);
    } catch (err: any) {
      console.error('Error fetching roles:', err);
    }
  }, [roleService]);

  /**
   * Fetch all rights/permissions
   */
  const fetchAllRights = useCallback(async () => {
    try {
      const url = getApiUrl('users', '/api/rights');
      const response = await httpClient.get<ResponseData<RightModelView[]>>(url);
      const rightsData = (response as any).data?.data || response.data || [];
      setAllRights(rightsData);
    } catch (err: any) {
      console.error('Error fetching rights:', err);
    }
  }, [httpClient]);

  /**
   * Fetch user devices
   */
  const fetchDevices = useCallback(async () => {
    if (!id) return;

    try {
      const url = getApiUrl('users', `/api/userpushdevice/user/${id}`);
      const response = await httpClient.get<ResponseData<UserPushDevice[]>>(url);
      const devicesData = (response as any).data?.data || response.data || [];
      setDevices(devicesData);
    } catch (err: any) {
      console.error('Error fetching devices:', err);
    }
  }, [id, httpClient]);

  /**
   * Fetch login attempts
   */
  const fetchLoginAttempts = useCallback(async () => {
    if (!id) return;

    try {
      const url = getApiUrl('users', `/api/audit/login-attempts?userId=${id}&page=1&pageSize=50`);
      const response = await httpClient.get<ResponseData<LoginAttemptModelView[]>>(url);
      const attemptsData = (response as any).data?.data || response.data || [];
      setLoginAttempts(attemptsData);

      // Get last successful login
      const lastSuccessful = attemptsData.find((a: LoginAttemptModelView) => a.success);
      setLastLogin(lastSuccessful || null);
    } catch (err: any) {
      console.error('Error fetching login attempts:', err);
    }
  }, [id, httpClient]);

  /**
   * Initial data load
   */
  useEffect(() => {
    Promise.all([
      fetchUser(),
      fetchUserRoles(),
      fetchAllRoles(),
      fetchAllRights(),
      fetchDevices(),
      fetchLoginAttempts(),
    ]);
  }, [fetchUser, fetchUserRoles, fetchAllRoles, fetchAllRights, fetchDevices, fetchLoginAttempts]);

  /**
   * Fetch and monitor presence status
   */
  useEffect(() => {
    if (!id) return;

    const fetchPresence = async () => {
      try {
        const presence = getUserPresence(id);
        if (presence) {
          setPresenceStatus(presence.status.toString().toLowerCase() as 'online' | 'away' | 'busy' | 'offline');
        } else {
          setPresenceStatus('offline');
        }
      } catch (error) {
        setPresenceStatus('offline');
      }
    };

    fetchPresence();

    // Real-time updates via onlineUsersMap
    const userPresence = onlineUsersMap.get(id);
    if (userPresence) {
      setPresenceStatus(userPresence.status.toString().toLowerCase() as 'online' | 'away' | 'busy' | 'offline');
    } else if (isConnected) {
      setPresenceStatus('offline');
    }
  }, [id, getUserPresence, onlineUsersMap, isConnected]);

  /**
   * Get presence badge color
   */
  const getPresenceColor = (status: 'online' | 'away' | 'busy' | 'offline') => {
    switch (status) {
      case 'online':
        return 'green';
      case 'away':
        return 'orange';
      case 'busy':
        return 'red';
      case 'offline':
      default:
        return 'gray';
    }
  };

  /**
   * Get presence label
   */
  const getPresenceLabel = (status: 'online' | 'away' | 'busy' | 'offline') => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  /**
   * Copy email to clipboard
   */
  const copyEmail = () => {
    if (user?.email) {
      navigator.clipboard.writeText(user.email);
      notifications.show({
        title: 'Copied',
        message: 'Email copied to clipboard',
        color: 'green',
      });
    }
  };

  /**
   * Format relative time
   */
  const formatRelativeTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-ZA', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  /**
   * Get user initials
   */
  const getInitials = () => {
    if (!user) return '';
    const first = user.firstName?.charAt(0) || '';
    const last = user.surname?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  };

  // Loading state
  if (loading && !user) {
    return (
      <Container size="xl" py="xl">
        <Center py="xl">
          <Loader />
        </Center>
      </Container>
    );
  }

  // Error state
  if (error && !user) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red" icon={<IconAlertCircle />} title="Error">
          {error}
        </Alert>
      </Container>
    );
  }

  // No user found
  if (!user) {
    return (
      <Container size="xl" py="xl">
        <Alert color="yellow" icon={<IconAlertCircle />} title="User Not Found">
          The requested user could not be found.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <FadeIn delay={100}>
        <Stack gap="xl">
          {/* Header Row: Back Button and Presence/Last Login */}
          <Group justify="space-between" wrap="wrap">
            <Group gap="sm">
              <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/admin/users')}>
                Back to Users
              </Button>
              {id && <ViewMemberProfileButton userId={id} />}
            </Group>
            <Stack gap={4} style={{ alignItems: 'flex-end' }}>
              <Badge color={getPresenceColor(presenceStatus)} size="lg" variant="light">
                {getPresenceLabel(presenceStatus)}
              </Badge>
              <Text size="xs" c="dimmed">
                Last login: {formatRelativeTime(lastLogin?.timestamp)}
              </Text>
            </Stack>
          </Group>

          {/* User Status Card */}
          <SlideIn direction="up" delay={150}>
            <Card withBorder p="lg">
              <Group justify="space-between" wrap="wrap" gap="lg">
                <Group gap="lg" style={{ flex: 1 }}>
                  <Avatar src={user.avatar} size={80} radius="md">
                    {getInitials()}
                  </Avatar>
                  <Stack gap="sm" style={{ flex: 1 }}>
                    <Group gap="sm">
                      <Text size="xl" fw={700}>
                        {user.fullName || `${user.firstName} ${user.surname}`}
                      </Text>
                    </Group>
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        {user.email}
                      </Text>
                      <ActionIcon size="xs" variant="subtle" onClick={copyEmail} aria-label="Copy email">
                        <IconCopy size={14} />
                      </ActionIcon>
                    </Group>
                    <Group gap="xs">
                      <Badge color={!user.isLockedOut ? 'green' : 'gray'} size="lg" variant="light">
                        {!user.isLockedOut ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge color={user.emailConfirmed ? 'blue' : 'orange'} size="lg" variant="light">
                        {user.emailConfirmed ? 'Confirmed' : 'Unconfirmed'}
                      </Badge>
                      {user.isLockedOut && (
                        <Badge color="red" size="lg" variant="filled">
                          Locked Out
                        </Badge>
                      )}
                    </Group>
                  </Stack>
                </Group>
                <Group gap="xl">
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Roles
                    </Text>
                    <Group gap="xs">
                      <IconShield size={20} color="gray" />
                      <Text size="xl" fw={700}>
                        {user.totalRoles || 0}
                      </Text>
                    </Group>
                  </Stack>
                  <Stack gap={4}>
                    <Text size="xs" c="dimmed" tt="uppercase">
                      Rights
                    </Text>
                    <Group gap="xs">
                      <IconKey size={20} color="gray" />
                      <Text size="xl" fw={700}>
                        {user.totalRights || 0}
                      </Text>
                    </Group>
                  </Stack>
                </Group>
              </Group>
            </Card>
          </SlideIn>

          {/* Tabs */}
          <SlideIn direction="up" delay={200}>
            <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'account-details')}>
              <Tabs.List>
                <Tabs.Tab value="account-details" leftSection={<IconUser size={16} />}>
                  Account Details
                </Tabs.Tab>
                <Tabs.Tab value="member-info" leftSection={<IconUserCircle size={16} />}>
                  Member Info
                </Tabs.Tab>
                <Tabs.Tab value="roles-rights" leftSection={<IconShield size={16} />}>
                  Roles & Rights
                </Tabs.Tab>
                <Tabs.Tab value="devices" leftSection={<IconDevices size={16} />}>
                  Devices
                </Tabs.Tab>
                <Tabs.Tab value="voip" leftSection={<IconPhone size={16} />}>
                  VoIP
                </Tabs.Tab>
                <Tabs.Tab value="login-activity" leftSection={<IconHistory size={16} />}>
                  Login Activity
                </Tabs.Tab>
                <Tabs.Tab value="danger-zone" leftSection={<IconAlertTriangle size={16} />}>
                  Danger Zone
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="account-details" pt="md">
                <AccountDetailsTab
                  user={user}
                  onUpdate={async () => {
                    await fetchUser();
                  }}
                />
              </Tabs.Panel>

              <Tabs.Panel value="member-info" pt="md">
                <MemberInfoTab
                  userId={id!}
                  userEmail={user.email || undefined}
                  userFirstName={user.firstName || undefined}
                  userSurname={user.surname || undefined}
                  userCellphone={user.cellphone || undefined}
                  onUpdate={async () => {
                    await fetchUser();
                  }}
                />
              </Tabs.Panel>

              <Tabs.Panel value="roles-rights" pt="md">
                <RolesRightsTab
                  userId={id!}
                  currentRoles={userRoles}
                  availableRoles={allRoles}
                  allRights={allRights}
                  onRolesUpdate={async () => {
                    await Promise.all([fetchUserRoles(), fetchUser()]);
                  }}
                />
              </Tabs.Panel>

              <Tabs.Panel value="devices" pt="md">
                <DevicesTab
                  userId={id!}
                  devices={devices}
                  onDeviceDelete={async () => {
                    await fetchDevices();
                  }}
                  onDeviceToggle={async () => {
                    await fetchDevices();
                  }}
                  onRefresh={fetchDevices}
                />
              </Tabs.Panel>

              <Tabs.Panel value="voip" pt="md">
                <VoipSetupTab userId={id!} />
              </Tabs.Panel>

              <Tabs.Panel value="login-activity" pt="md">
                <LoginActivityTab
                  userId={id!}
                  loginAttempts={loginAttempts}
                  onRefresh={fetchLoginAttempts}
                />
              </Tabs.Panel>

              <Tabs.Panel value="danger-zone" pt="md">
                <DangerZoneTab
                  userId={id!}
                  userEmail={user.email || ''}
                  isLockedOut={user.isLockedOut || false}
                  lockoutEnabled={user.lockoutEnabled || false}
                  onUserDeleted={() => {
                    navigate('/admin/users');
                  }}
                  onUserLockToggled={async () => {
                    await fetchUser();
                  }}
                  onPasswordReset={async () => {
                    // Password reset email sent, no action needed
                  }}
                />
              </Tabs.Panel>
            </Tabs>
          </SlideIn>
        </Stack>
      </FadeIn>
    </Container>
  );
}
